import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { WHATSAPP_NUMBER } from '../../constants';
import { useCart } from '../contexts/CartContext';
import { AdminProduct, db, StoreConfig } from '../services/backend';
import { trackPixelEvent } from '../utils/pixel';

interface FloatingWhatsAppButtonProps {
  config?: StoreConfig | null;
}

const DEFAULT_MESSAGE = 'Ola, vim pelo site da Veraz e gostaria de atendimento.';
const DEFAULT_PRODUCT_TEMPLATE =
  'Ola, vim pelo site da Veraz e gostaria de ajuda com o produto {produto}. Link: {link}';

const WhatsAppLogo = ({ className = '' }: { className?: string }) => (
  <svg viewBox="0 0 32 32" aria-hidden="true" className={className} focusable="false">
    <path
      fill="currentColor"
      d="M16.04 3.2C8.99 3.2 3.26 8.85 3.26 15.8c0 2.23.6 4.4 1.72 6.31L3.14 28.8l6.87-1.79a12.9 12.9 0 0 0 6.03 1.51c7.05 0 12.78-5.65 12.78-12.61S23.09 3.2 16.04 3.2Zm0 22.98a10.55 10.55 0 0 1-5.38-1.46l-.38-.23-4.08 1.06 1.09-3.92-.25-.4a10.2 10.2 0 0 1-1.55-5.43c0-5.67 4.73-10.28 10.55-10.28 5.81 0 10.54 4.61 10.54 10.28 0 5.67-4.73 10.38-10.54 10.38Z"
    />
    <path
      fill="currentColor"
      d="M21.83 18.53c-.32-.16-1.91-.93-2.21-1.04-.3-.1-.52-.16-.73.16-.22.32-.84 1.03-1.03 1.24-.19.22-.38.24-.7.08-.32-.16-1.36-.49-2.59-1.57-.96-.84-1.61-1.87-1.8-2.19-.19-.32-.02-.49.14-.65.15-.14.32-.38.49-.57.16-.19.22-.32.32-.54.11-.22.06-.41-.03-.57-.08-.16-.73-1.74-1-2.39-.26-.62-.53-.54-.73-.55h-.62c-.22 0-.57.08-.87.41-.3.32-1.14 1.1-1.14 2.68s1.17 3.11 1.33 3.33c.16.22 2.3 3.47 5.58 4.86.78.33 1.39.53 1.86.68.78.24 1.49.21 2.05.13.63-.09 1.91-.77 2.18-1.51.27-.75.27-1.38.19-1.51-.08-.13-.3-.21-.62-.37Z"
    />
  </svg>
);

const formatMoney = (value?: number) => {
  if (!value || value <= 0) return '';
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
};

const fillProductTemplate = (template: string, product: AdminProduct, productUrl: string) => {
  const price = formatMoney(product.salePrice || product.price);

  return template
    .replace(/\{produto\}/g, product.name)
    .replace(/\{nome\}/g, product.name)
    .replace(/\{link\}/g, productUrl)
    .replace(/\{url\}/g, productUrl)
    .replace(/\{preco\}/g, price || 'sob consulta')
    .replace(/\{sku\}/g, product.sku || '');
};

const FloatingWhatsAppButton: React.FC<FloatingWhatsAppButtonProps> = ({ config }) => {
  const { cart } = useCart();
  const location = useLocation();
  const [product, setProduct] = useState<AdminProduct | null>(null);

  const buttonConfig = config?.floatingWhatsapp;
  const productId = useMemo(() => {
    const match = location.pathname.match(/^\/produto\/([^/?#]+)/);
    return match?.[1] ? decodeURIComponent(match[1]) : '';
  }, [location.pathname]);

  useEffect(() => {
    let mounted = true;

    if (!productId) {
      setProduct(null);
      return () => {
        mounted = false;
      };
    }

    const loadProduct = async () => {
      try {
        const productData = await db.getProduct(productId);
        if (mounted) setProduct(productData || null);
      } catch (error) {
        console.error('Erro ao carregar produto para WhatsApp flutuante:', error);
        if (mounted) setProduct(null);
      }
    };

    loadProduct();

    return () => {
      mounted = false;
    };
  }, [productId]);

  if (!config || buttonConfig?.enabled === false) return null;

  const contactWhatsapp = config.contacts?.whatsapp || WHATSAPP_NUMBER;
  const configuredNumber =
    buttonConfig?.numberMode === 'custom' && buttonConfig.customNumber
      ? buttonConfig.customNumber
      : contactWhatsapp;
  const whatsappDigits = configuredNumber.replace(/\D/g, '');

  if (!whatsappDigits) return null;

  const hasFloatingCart =
    cart.length > 0 &&
    location.pathname !== '/carrinho' &&
    !location.pathname.startsWith('/checkout');
  const bottomClass = hasFloatingCart ? 'bottom-28 md:bottom-24' : 'bottom-5 md:bottom-6';
  const isProductPage = Boolean(productId && product);
  const productUrl =
    typeof window !== 'undefined' ? `${window.location.origin}${location.pathname}` : '';

  const message =
    isProductPage && buttonConfig?.productMessageEnabled !== false && product
      ? fillProductTemplate(
          buttonConfig?.productMessageTemplate || DEFAULT_PRODUCT_TEMPLATE,
          product,
          productUrl
        )
      : buttonConfig?.message || DEFAULT_MESSAGE;

  const helpText =
    isProductPage && buttonConfig?.showProductHelpText !== false
      ? buttonConfig?.productHelpText?.trim() || 'Precisa de ajuda?'
      : 'Precisa de ajuda?';

  const handleClick = () => {
    trackPixelEvent('Contact', {
      type: isProductPage ? 'whatsapp_float_product' : 'whatsapp_float',
      product_id: product?.id,
      content_name: product?.name,
    });
    db.incrementWhatsappClicks(isProductPage && product ? product.id : 'floating-whatsapp').catch(
      () => undefined
    );
  };

  return (
    <div
      className={`group fixed right-4 ${bottomClass} z-[95] flex h-[74px] w-[74px] items-center justify-center md:right-6`}
      aria-live="polite"
    >
      <span
        aria-hidden="true"
        className="pointer-events-none absolute bottom-[calc(100%+8px)] left-1/2 z-10 -translate-x-1/2 translate-y-2 scale-95 whitespace-nowrap rounded-full bg-white px-3 py-1.5 text-[12px] font-bold leading-none text-[#128c45] opacity-0 shadow-[0_14px_30px_rgba(35,31,32,0.16)] blur-sm transition-all duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] after:absolute after:left-1/2 after:top-full after:-translate-x-1/2 after:border-x-[7px] after:border-t-[8px] after:border-x-transparent after:border-t-white group-hover:translate-y-0 group-hover:scale-100 group-hover:opacity-100 group-hover:blur-0 group-focus-within:translate-y-0 group-focus-within:scale-100 group-focus-within:opacity-100 group-focus-within:blur-0"
      >
        {helpText}
      </span>

      <a
        href={`https://wa.me/${whatsappDigits}?text=${encodeURIComponent(message)}`}
        target="_blank"
        rel="noopener noreferrer"
        onClick={handleClick}
        className="flex h-[74px] w-[74px] shrink-0 items-center justify-center rounded-full bg-[#25D366] text-white shadow-[0_18px_40px_rgba(37,211,102,0.28)] transition-transform hover:scale-105 focus:outline-none focus:ring-4 focus:ring-[#25D366]/25"
        aria-label="Falar com a Veraz no WhatsApp"
      >
        <WhatsAppLogo className="h-10 w-10" />
      </a>
    </div>
  );
};

export default FloatingWhatsAppButton;
